import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Microphone, ArrowRight, Scales, ShareNetwork, ClockCounterClockwise, UsersThree, ArrowSquareOut, Sparkle } from "@phosphor-icons/react";
import { Brand } from "@/components/Brand";
import { SaleBanner } from "@/components/SaleBanner";
import { DiwaliPack } from "@/components/DiwaliPack";
import { getConfig } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STEPS = [
  ["1", "Say it", "Tap the mic and describe what you want, the way you'd explain it to a friend."],
  ["2", "We write it", "SpeakToPrompt turns your words into a clear, structured prompt using proven prompting techniques."],
  ["3", "Paste & go", "One tap opens ChatGPT, Claude, Gemini and more with your prompt ready to send."],
];
const FEATURES = [
  [Scales, "Compare AIs", "See how GPT, Claude and Gemini write the same prompt, side by side."],
  [ClockCounterClockwise, "Version history", "Every save is a version. See what changed and roll back anytime."],
  [UsersThree, "Team library", "Share your best prompts with your team using a simple invite code."],
  [ShareNetwork, "Share links", "Send any prompt as a link that anyone can open and copy."],
  [ArrowSquareOut, "Open anywhere", "Made for ChatGPT, Claude, Gemini, Midjourney, Sora, Perplexity and more."],
  [Sparkle, "Quality score", "Every prompt comes with a score, the techniques used, and tips to improve it."],
];
const TOOLS = ["ChatGPT", "Claude", "Gemini", "Midjourney", "DALL·E", "Sora", "Perplexity", "Grok", "Stable Diffusion"];
const fade = (d = 0) => ({ initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6, delay: d } });

function HeroMic() {
  return (
    <div className="relative mx-auto mt-16 flex h-64 w-64 items-center justify-center">
      <div className="stp-siri absolute h-48 w-48 rounded-full opacity-60 blur-3xl" />
      <div className="relative flex h-40 w-40 items-center justify-center rounded-full bg-ink text-white shadow-[0_30px_60px_-15px_rgba(0,0,0,0.45)]">
        <Microphone size={64} weight="fill" />
      </div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sale, setSale] = useState(null);
  useEffect(() => {
    getConfig().then((c) => setSale(c.sale)).catch(() => {});
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("stp_ref", ref);
  }, []);
  const go = () => navigate(user ? "/app" : "/login");

  return (
    <div className="stp-bg">
      <SaleBanner sale={sale} />
      <header className="stp-glass sticky top-0 z-30 border-x-0 border-t-0 border-b border-black/5">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-5 sm:px-8">
          <Brand />
          <nav className="flex items-center gap-1">
            <Link to="/pricing" data-testid="nav-pricing" className="rounded-full px-3 py-1.5 text-sm text-ink hover:bg-black/5">Pricing</Link>
            <button data-testid="landing-cta-top" onClick={go} className="rounded-full bg-ink px-4 py-1.5 text-sm font-medium text-white hover:bg-black">{user ? "Open app" : "Sign in"}</button>
          </nav>
        </div>
      </header>

      <section className="mx-auto max-w-5xl px-5 pb-24 pt-20 text-center sm:px-8 sm:pt-28">
        <motion.p {...fade()} className="text-sm font-semibold text-brand">7-day free trial · no credit card{sale?.active ? ` · ${sale.percent}% off with ${sale.code}` : ""}</motion.p>
        <motion.h1 {...fade(0.05)} className="mt-4 font-display text-5xl font-semibold leading-[1.02] text-ink sm:text-6xl lg:text-7xl">
          Say it.<br /><span className="bg-gradient-to-r from-brand via-[#5E5CE6] to-[#FF2D55] bg-clip-text text-transparent">Get the perfect prompt.</span>
        </motion.h1>
        <motion.p {...fade(0.1)} className="mx-auto mt-6 max-w-2xl text-lg text-mist sm:text-xl">
          Tell SpeakToPrompt what you want to build, in your own words. It writes the expert prompt for ChatGPT, Claude, Gemini, Midjourney and more.
        </motion.p>
        <motion.div {...fade(0.15)} className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <button data-testid="landing-cta-main" onClick={go} className="flex h-12 items-center gap-2 rounded-full bg-brand px-7 text-base font-semibold text-white shadow-[0_12px_30px_-10px_rgba(0,113,227,0.7)] hover:bg-brand-hover active:scale-[0.98]">
            Start speaking free <ArrowRight size={18} weight="bold" />
          </button>
          <Link to="/pricing" data-testid="landing-pricing-link" className="flex h-12 items-center rounded-full px-6 text-base font-medium text-brand hover:underline">See pricing ›</Link>
        </motion.div>
        <motion.div {...fade(0.2)}><HeroMic /></motion.div>
        <motion.div {...fade(0.25)} className="mt-10 flex flex-wrap justify-center gap-2">
          {TOOLS.map((t) => <span key={t} className="rounded-full bg-white px-3.5 py-1.5 text-xs font-medium text-mist shadow-[inset_0_0_0_1px_rgba(0,0,0,0.06)]">{t}</span>)}
        </motion.div>
      </section>

      <section className="bg-white py-28">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <motion.h2 {...fade()} className="max-w-2xl font-display text-4xl font-semibold leading-tight text-ink sm:text-5xl">Three steps. No prompt skills needed.</motion.h2>
          <div className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-3">
            {STEPS.map(([n, t, d], i) => (
              <motion.div key={n} {...fade(i * 0.08)}>
                <span className="font-display text-6xl font-semibold text-brand/20">{n}</span>
                <h3 className="mt-2 font-display text-2xl font-semibold text-ink">{t}</h3>
                <p className="mt-2 text-base leading-relaxed text-mist">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {sale?.active && <DiwaliPack />}

      <section className="bg-white py-28">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <motion.h2 {...fade()} className="max-w-2xl font-display text-4xl font-semibold leading-tight text-ink sm:text-5xl">Everything you need to get great answers from AI.</motion.h2>
          <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(([Icon, t, d], i) => (
              <motion.div key={t} {...fade((i % 3) * 0.06)} className="stp-card rounded-3xl p-8 transition-transform duration-300 hover:-translate-y-1">
                <Icon size={30} weight="duotone" className="text-brand" />
                <h3 className="mt-5 font-display text-xl font-semibold text-ink">{t}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-mist">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 pb-28 sm:px-8">
        <motion.div {...fade()} className="mx-auto max-w-4xl rounded-[2.5rem] bg-ink px-8 py-16 text-center sm:px-16">
          <h2 className="font-display text-4xl font-semibold text-white sm:text-5xl">Your next idea is one sentence away.</h2>
          <p className="mt-4 text-lg text-white/60">7 days free. Every model. Unlimited prompts.</p>
          <button data-testid="landing-cta-bottom" onClick={go} className="mt-8 inline-flex h-12 items-center gap-2 rounded-full bg-white px-7 font-semibold text-ink hover:bg-white/90">
            Try SpeakToPrompt <ArrowRight size={18} weight="bold" />
          </button>
        </motion.div>
      </section>

      <footer className="border-t border-black/5 py-8 text-center text-xs text-mist">© SpeakToPrompt · Speak your idea, get the perfect AI prompt</footer>
    </div>
  );
}
